// ===========================================
// 💰 COMPONENTE DE INGRESOS COMPLETO CON SOPORTE MÓVIL OPTIMIZADO: src/components/AGTFin/dashboard/incomes/FinIncome.js
// ===========================================

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Fab,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  WorkOutline as WorkIcon,
  CardGiftcard as GiftIcon,
  Analytics as AnalyticsIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import agtFinService from '../../services/agtFinService';

// ===========================================
// 🎬 VARIANTES DE ANIMACIÓN TIPO macOS
// ===========================================
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const parseLocalDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const [year, month, day] = dateString.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const normalizeDateInput = (value) => {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === 'string') {
    return parseLocalDate(value);
  }

  return null;
};

const formatLocalDateString = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return null;
  }

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ===========================================
// 🎨 PALETA DE COLORES EN ESCALA DE GRISES
// ===========================================
const getGrayscalePalette = (theme) => ({
  // Colores principales en escala de grises
  primary: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
  secondary: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
  tertiary: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161',
  quaternary: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575',
  
  // Gradientes para elementos visuales
  gradients: {
    primary: theme.palette.mode === 'dark' 
      ? 'linear-gradient(180deg, #FFFFFF 0%, #E0E0E0 100%)'
      : 'linear-gradient(180deg, #000000 0%, #424242 100%)',
    
    secondary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #E0E0E0 0%, #BDBDBD 100%)'
      : 'linear-gradient(180deg, #424242 0%, #616161 100%)',
    
    tertiary: theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg, #BDBDBD 0%, #9E9E9E 100%)'
      : 'linear-gradient(180deg, #616161 0%, #757575 100%)',
    
    card: theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
      : 'linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(248,249,250,1) 100%)'
  },
  
  // Colores para estados
  success: theme.palette.mode === 'dark' ? '#E0E0E0' : '#424242',
  warning: theme.palette.mode === 'dark' ? '#BDBDBD' : '#616161',
  error: theme.palette.mode === 'dark' ? '#9E9E9E' : '#757575',
  info: theme.palette.mode === 'dark' ? '#757575' : '#9E9E9E',
  
  // Opacidades para diferentes elementos
  backgrounds: {
    light: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    medium: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
    dark: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)',
    card: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)'
  }
});

// ===========================================
// 🎯 COMPONENTES ESTILIZADOS OPTIMIZADOS PARA MÓVIL
// ===========================================

// Contenedor principal adaptativo
const ResponsiveContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 24,
  width: '100%',
  maxWidth: 1200,
  margin: '0 auto',
  background: theme.palette.mode === 'dark' 
    ? 'rgba(0,0,0,0.2)' 
    : 'rgba(255,255,255,0.8)',
  backdropFilter: 'blur(20px)',
  border: theme.palette.mode === 'dark' 
    ? '1px solid rgba(255,255,255,0.1)' 
    : '1px solid rgba(0,0,0,0.05)',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
    borderRadius: 16,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    margin: theme.spacing(0.5),
    width: 'calc(100% - 16px)',
  }
}));

// Tarjeta de estadísticas responsive
const MobileStatsCard = styled(Card)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 20,
    background: colors.backgrounds.card,
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255,255,255,0.1)' 
      : '1px solid rgba(0,0,0,0.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 12px 40px rgba(0,0,0,0.4)'
        : '0 12px 40px rgba(0,0,0,0.15)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: colors.gradients.primary,
    },
    [theme.breakpoints.down('sm')]: {
      borderRadius: 16,
      '&:hover': {
        transform: 'none', // Desactivar hover en móvil
      }
    }
  };
});

// Botón FAB para móvil - NUEVO: Posicionado al lado del título
const MobileFab = styled(Fab)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    background: colors.gradients.primary,
    color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 24px rgba(255,255,255,0.2)'
      : '0 8px 24px rgba(0,0,0,0.2)',
    '&:hover': {
      background: colors.gradients.secondary,
      transform: 'scale(1.1)',
    },
    width: 48,
    height: 48,
    minHeight: 'auto'
  };
});

// Botón adaptativo
const ResponsiveButton = styled(Button)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 16,
    padding: '12px 24px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    background: colors.gradients.primary,
    color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
    border: 'none',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 16px rgba(255,255,255,0.1)'
      : '0 4px 16px rgba(0,0,0,0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 24px rgba(255,255,255,0.15)'
        : '0 8px 24px rgba(0,0,0,0.15)',
      background: colors.gradients.secondary,
    },
    '&:active': {
      transform: 'translateY(0px)',
    },
    '&:disabled': {
      opacity: 0.6,
      transform: 'none',
      boxShadow: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '10px 16px',
      fontSize: '13px',
      borderRadius: 12,
      minWidth: 'auto',
      '&:hover': {
        transform: 'none', // Desactivar hover en móvil
      }
    }
  };
});

// Tabla responsive que se convierte en cards en móvil
const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 20,
    background: colors.backgrounds.card,
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255,255,255,0.1)' 
      : '1px solid rgba(0,0,0,0.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.2)'
      : '0 8px 32px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    '& .MuiTableHead-root': {
      background: colors.backgrounds.medium,
    },
    '& .MuiTableCell-head': {
      fontWeight: 600,
      fontSize: '14px',
      color: colors.primary,
      borderBottom: `1px solid ${colors.backgrounds.medium}`,
    },
    '& .MuiTableRow-root': {
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: colors.backgrounds.light,
      }
    },
    '& .MuiTableCell-body': {
      borderBottom: `1px solid ${colors.backgrounds.light}`,
      fontSize: '14px',
    },
    [theme.breakpoints.down('md')]: {
      borderRadius: 16,
      '& .MuiTableCell-head': {
        fontSize: '13px',
        padding: '12px 8px',
      },
      '& .MuiTableCell-body': {
        fontSize: '13px',
        padding: '12px 8px',
      }
    }
  };
});

// Card móvil para ingresos
const MobileIncomeCard = styled(Card)(({ theme }) => {
  const colors = getGrayscalePalette(theme);
  return {
    borderRadius: 16,
    background: colors.backgrounds.card,
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255,255,255,0.1)' 
      : '1px solid rgba(0,0,0,0.05)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 6px 24px rgba(0,0,0,0.2)'
      : '0 6px 24px rgba(0,0,0,0.08)',
    marginBottom: theme.spacing(2),
    transition: 'all 0.2s ease',
    '&:active': {
      transform: 'scale(0.98)',
    }
  };
});

// Diálogo fullscreen para móvil
const MobileDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiDialog-paper': {
      borderRadius: 24,
      background: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : (theme.palette.mode === 'dark'
          ? 'rgba(30,30,30,0.95)'
          : 'rgba(255,255,255,0.95)'),
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: isWallpaperMode
        ? '1px solid rgba(255, 255, 255, 0.2)'
        : (theme.palette.mode === 'dark'
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(0,0,0,0.05)'),
      boxShadow: 'none',
      [theme.breakpoints.down('sm')]: {
        margin: 8,
        width: 'calc(100% - 16px)',
        maxHeight: 'calc(100% - 16px)',
        borderRadius: 16,
      }
    }
  };
});

// TextField responsive - ACTUALIZADO: Sin borde para búsqueda
const ResponsiveTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'noborder'
})(({ theme, noborder }) => {
  const colors = getGrayscalePalette(theme);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: colors.backgrounds.light,
      transition: 'all 0.3s ease',
      ...(noborder && {
        border: 'none',
        '& fieldset': {
          border: 'none',
        },
        '&:hover fieldset': {
          border: 'none',
        },
        '&.Mui-focused fieldset': {
          border: 'none',
        },
        '&:hover': {
          background: colors.backgrounds.light,
        },
        '&.Mui-focused': {
          background: colors.backgrounds.light,
        }
      }),
      ...(!noborder && {
        '&:hover': {
          background: colors.backgrounds.medium,
        },
        '&.Mui-focused': {
          background: colors.backgrounds.medium,
          '& fieldset': {
            borderColor: colors.primary,
            borderWidth: 2,
          }
        },
        '& fieldset': {
          borderColor: colors.backgrounds.dark,
          transition: 'all 0.3s ease',
        }
      })
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
        fontSize: '16px', // Evita zoom en iOS
      }
    }
  };
});

// Chip adaptativo - ACTUALIZADO: Tamaño de texto fijo
const ResponsiveChip = styled(Chip)(({ theme, chiptype }) => {
  const colors = getGrayscalePalette(theme);
  
  const getChipStyles = () => {
    switch (chiptype) {
      case 'paycheck':
        return {
          background: colors.gradients.primary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      case 'extra':
        return {
          background: colors.gradients.secondary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      case 'other':
        return {
          background: colors.gradients.tertiary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
      default:
        return {
          background: colors.gradients.primary,
          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
        };
    }
  };

  return {
    borderRadius: 12,
    fontWeight: 600,
    fontSize: '11px !important', // Forzar tamaño fijo
    height: 24,
    ...getChipStyles(),
    '& .MuiChip-label': {
      fontSize: '11px !important', // Forzar tamaño del label
      padding: '0 8px',
      fontWeight: 600,
    },
    '& .MuiChip-icon': {
      color: 'inherit',
      fontSize: '14px !important',
    },
    [theme.breakpoints.down('sm')]: {
      height: 22,
      fontSize: '10px !important',
      borderRadius: 10,
      '& .MuiChip-label': {
        fontSize: '10px !important',
        padding: '0 6px',
      },
      '& .MuiChip-icon': {
        fontSize: '12px !important',
      }
    }
  };
});

// ===========================================
// 📊 SECCIÓN DE TOTALES OPTIMIZADA PARA MÓVIL
// ===========================================
const MobileTotalsSection = ({ summary, userProfile, theme }) => {
  const { t } = useTranslation();
  const colors = getGrayscalePalette(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const formatCurrency = (amount) => {
    return agtFinService.formatCurrency(amount || 0, userProfile?.currency || 'USD');
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant={isMobile ? "h6" : "h5"}
        fontWeight={700}
        sx={{
          mb: 2,
          color: colors.primary,
          textAlign: isMobile ? 'center' : 'left'
        }}
      >
        {t('agtfin.incomes.totals')}
      </Typography>
      
      <MobileStatsCard>
        <CardContent sx={{ p: isMobile ? 2.5 : 4 }}>
          <Grid container spacing={isMobile ? 2 : 4} alignItems="center">
            {/* Total General - Destacado en móvil */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? 2 : 3,
                flexDirection: isMobile ? 'column' : 'row',
                textAlign: isMobile ? 'center' : 'left'
              }}>
                <Box sx={{
                  p: isMobile ? 2 : 2.5,
                  borderRadius: '20px',
                  background: colors.gradients.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <MoneyIcon sx={{ 
                    fontSize: isMobile ? 32 : 36, 
                    color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                  }} />
                </Box>
                <Box>
                  <Typography
                    variant={isMobile ? "body2" : "subtitle1"}
                    color="text.secondary"
                    fontWeight={500}
                    sx={{ mb: 0.5 }}
                  >
                    {t('agtfin.incomes.totalIncome')}
                  </Typography>
                  <Typography 
                    variant={isMobile ? "h4" : "h3"} 
                    component="div" 
                    fontWeight={700} 
                    sx={{ color: colors.primary }}
                  >
                    {formatCurrency(summary.total)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Desglose por categorías */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: isMobile ? 1.5 : 2,
                height: '100%',
                justifyContent: 'center'
              }}>
                {/* Items de desglose con diseño móvil */}
                {[
                  {
                    icon: WorkIcon,
                    labelKey: 'agtfin.incomes.types.paycheck',
                    value: summary.paycheck,
                    gradient: colors.gradients.primary
                  },
                  {
                    icon: TrendingUpIcon,
                    labelKey: 'agtfin.incomes.types.extra',
                    value: summary.extra,
                    gradient: colors.gradients.secondary
                  },
                  {
                    icon: GiftIcon,
                    labelKey: 'agtfin.incomes.types.other',
                    value: summary.other,
                    gradient: colors.gradients.tertiary
                  }
                ].map((item, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      p: isMobile ? 1.5 : 1,
                      borderRadius: isMobile ? 2 : 1,
                      background: isMobile ? colors.backgrounds.light : 'transparent',
                      '&:hover': {
                        background: colors.backgrounds.medium,
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        p: isMobile ? 1.5 : 1,
                        borderRadius: '8px',
                        background: item.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <item.icon sx={{ 
                          fontSize: isMobile ? 18 : 16, 
                          color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                        }} />
                      </Box>
                      <Typography
                        variant={isMobile ? "body2" : "body1"}
                        fontWeight={600}
                      >
                        {t(item.labelKey)}
                      </Typography>
                    </Box>
                    <Typography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      fontWeight={700} 
                      sx={{ color: colors.primary }}
                    >
                      {formatCurrency(item.value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </MobileStatsCard>
    </Box>
  );
};

// ===========================================
// 📊 DISTRIBUCIÓN VISUAL ADAPTATIVA
// ===========================================
const MobileDistributionSection = ({ summary, userProfile, theme }) => {
  const { t } = useTranslation();
  const colors = getGrayscalePalette(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(!isMobile); // Colapsado por defecto en móvil
  
  const metrics = [
    {
      label: t('agtfin.incomes.types.paycheck'),
      value: summary.paycheck || 0,
      icon: <WorkIcon />,
      gradient: colors.gradients.primary,
      percentage: summary.total > 0 ? ((summary.paycheck || 0) / summary.total * 100) : 0
    },
    {
      label: t('agtfin.incomes.types.extra'),
      value: summary.extra || 0,
      icon: <TrendingUpIcon />,
      gradient: colors.gradients.secondary,
      percentage: summary.total > 0 ? ((summary.extra || 0) / summary.total * 100) : 0
    },
    {
      label: t('agtfin.incomes.types.other'),
      value: summary.other || 0,
      icon: <GiftIcon />,
      gradient: colors.gradients.tertiary,
      percentage: summary.total > 0 ? ((summary.other || 0) / summary.total * 100) : 0
    }
  ];

  if (isMobile) {
    return (
      <Box sx={{ mb: 3 }}>
        <Accordion 
          expanded={expanded} 
          onChange={() => setExpanded(!expanded)}
          sx={{
            background: colors.backgrounds.card,
            borderRadius: 2,
            '&:before': { display: 'none' },
            boxShadow: theme.palette.mode === 'dark'
              ? '0 6px 24px rgba(0,0,0,0.2)'
              : '0 6px 24px rgba(0,0,0,0.08)',
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon sx={{ color: colors.primary }} />}
            sx={{ 
              '& .MuiAccordionSummary-content': { 
                alignItems: 'center' 
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AnalyticsIcon sx={{ color: colors.primary, fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600} sx={{ color: colors.primary }}>
                {t('agtfin.incomes.distribution')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Grid container spacing={2}>
              {metrics.map((metric, index) => (
                <Grid item xs={12} key={index}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    background: colors.backgrounds.light,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {React.cloneElement(metric.icon, { 
                        fontSize: 'medium',
                        sx: { color: colors.primary }
                      })}
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {metric.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {metric.percentage.toFixed(1)}% {t('agtfin.incomes.percentOfTotal')}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Box sx={{
                        width: 40,
                        height: `${Math.max(Math.min(metric.percentage, 100), 8)}px`,
                        maxHeight: 40,
                        background: metric.gradient,
                        borderRadius: 1,
                        mb: 0.5,
                        ml: 'auto'
                      }} />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }

  // Vista desktop original
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3, color: colors.primary }}>
        {t('agtfin.incomes.distribution')}
      </Typography>
      
      <Box sx={{ 
        p: 4, 
        borderRadius: 4, 
        background: colors.backgrounds.card,
        border: theme.palette.mode === 'dark' 
          ? '1px solid rgba(255,255,255,0.1)' 
          : '1px solid rgba(0,0,0,0.05)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 8px 32px rgba(0,0,0,0.2)'
          : '0 8px 32px rgba(0,0,0,0.08)',
      }}>
        <Grid container spacing={4}>
          {metrics.map((metric, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mb: 3
                }}>
                  {React.cloneElement(metric.icon, { 
                    fontSize: 'large',
                    sx: { color: colors.primary, mr: 1 }
                  })}
                  <Typography variant="subtitle1" fontWeight={600}>
                    {metric.label}
                  </Typography>
                </Box>
                
                {/* Barra de progreso visual */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ 
                    height: 120, 
                    display: 'flex', 
                    alignItems: 'end', 
                    justifyContent: 'center',
                    mb: 2
                  }}>
                    <Box sx={{
                      width: 50,
                      height: `${Math.max(metric.percentage, 8)}%`,
                      background: metric.gradient,
                      borderRadius: '12px 12px 0 0',
                      transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: -4,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: metric.gradient,
                      }
                      }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: colors.primary, mb: 1 }}>
                    {metric.percentage.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('agtfin.incomes.percentOfTotal')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

// ===========================================
// 📱 COMPONENTE DE LISTA MÓVIL PARA INGRESOS
// ===========================================
const MobileIncomeList = ({ 
  incomes, 
  formatCurrency, 
  getIncomeTypeIcon, 
  getIncomeTypeName, 
  handleEditIncome, 
  handleDeleteIncome, 
  submitLoading,
  colors,
  theme
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('list'); // 'list' o 'grid'

  if (incomes.length === 0) {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        py: 8,
        px: 2
      }}>
        <Box sx={{
          p: 3,
          borderRadius: '50%',
          background: colors.backgrounds.light,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3
        }}>
          <MoneyIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          {t('agtfin.incomes.empty')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('agtfin.incomes.emptyHelp')}
        </Typography>
      </Box>
    );
  }

  if (viewMode === 'grid') {
    return (
      <Box>
        {/* Toggle de vista */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          mb: 2,
          gap: 1
        }}>
          <IconButton 
            size="small"
            onClick={() => setViewMode('list')}
            sx={{
              background: viewMode === 'list' ? colors.backgrounds.medium : 'transparent',
              borderRadius: 2
            }}
          >
            <ViewListIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small"
            onClick={() => setViewMode('grid')}
            sx={{
              background: viewMode === 'grid' ? colors.backgrounds.medium : 'transparent',
              borderRadius: 2
            }}
          >
            <ViewModuleIcon fontSize="small" />
          </IconButton>
        </Box>

        <Grid container spacing={2}>
          {incomes.map((income) => (
            <Grid item xs={12} sm={6} key={income.incomeId}>
              <MobileIncomeCard>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                      <Box sx={{
                        p: 1,
                        borderRadius: 2,
                        background: colors.backgrounds.medium,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {getIncomeTypeIcon(income.incomeType)}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600} noWrap>
                          {income.name}
                        </Typography>
                        <ResponsiveChip 
                          label={getIncomeTypeName(income.incomeType)}
                          size="small"
                          chiptype={income.incomeType}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                    <IconButton 
                      size="small"
                      onClick={() => handleDeleteIncome(income.incomeId)}
                      disabled={submitLoading}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: colors.primary }}>
                      {formatCurrency(income.amount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {parseLocalDate(income.date)
                        ? format(parseLocalDate(income.date), 'dd/MM/yy')
                        : income.date}
                    </Typography>
                  </Box>
                  
                  {income.description && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mb: 1
                      }}
                    >
                      {income.description}
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ResponsiveButton 
                      size="small"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={() => handleEditIncome(income)}
                      disabled={submitLoading}
                      sx={{ minWidth: 'auto' }}
                    >
                      {t('agtfin.common.edit')}
                    </ResponsiveButton>
                  </Box>
                </CardContent>
              </MobileIncomeCard>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Vista de lista
  return (
    <Box>
      {/* Toggle de vista */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        mb: 2,
        gap: 1
      }}>
        <IconButton 
          size="small"
          onClick={() => setViewMode('list')}
          sx={{
            background: viewMode === 'list' ? colors.backgrounds.medium : 'transparent',
            borderRadius: 2
          }}
        >
          <ViewListIcon fontSize="small" />
        </IconButton>
        <IconButton 
          size="small"
          onClick={() => setViewMode('grid')}
          sx={{
            background: viewMode === 'grid' ? colors.backgrounds.medium : 'transparent',
            borderRadius: 2
          }}
        >
          <ViewModuleIcon fontSize="small" />
        </IconButton>
      </Box>

      <List sx={{ p: 0 }}>
        {incomes.map((income, index) => (
          <React.Fragment key={income.incomeId}>
            <ListItem
              sx={{
                borderRadius: 2,
                mb: 1,
                background: colors.backgrounds.card,
                border: theme.palette.mode === 'dark' 
                  ? '1px solid rgba(255,255,255,0.1)' 
                  : '1px solid rgba(0,0,0,0.05)',
                '&:hover': {
                  background: colors.backgrounds.medium,
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 2,
                  background: colors.backgrounds.medium,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {getIncomeTypeIcon(income.incomeType)}
                </Box>
              </ListItemIcon>
              
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {income.name}
                    </Typography>
                    <ResponsiveChip 
                      label={getIncomeTypeName(income.incomeType)}
                      size="small"
                      chiptype={income.incomeType}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body1" fontWeight={700} sx={{ color: colors.primary, mb: 0.5 }}>
                      {formatCurrency(income.amount)}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {parseLocalDate(income.date)
                          ? format(parseLocalDate(income.date), 'dd/MM/yyyy')
                          : income.date}
                      </Typography>
                      {income.description && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            maxWidth: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {income.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                }
              />
              
              <ListItemSecondaryAction>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton 
                    size="small"
                    onClick={() => handleEditIncome(income)}
                    disabled={submitLoading}
                    sx={{
                      background: colors.backgrounds.light,
                      '&:hover': { background: colors.backgrounds.medium }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small"
                    onClick={() => handleDeleteIncome(income.incomeId)}
                    disabled={submitLoading}
                    sx={{
                      background: colors.backgrounds.light,
                      '&:hover': { background: colors.backgrounds.medium }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItemSecondaryAction>
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

// Tipos de ingresos disponibles - se mantendrán los labels hardcodeados aquí para el select
const incomeTypes = [
  { value: 'paycheck', labelKey: 'agtfin.incomes.types.paycheck', icon: <WorkIcon /> },
  { value: 'extra', labelKey: 'agtfin.incomes.types.extra', icon: <TrendingUpIcon /> },
  { value: 'other', labelKey: 'agtfin.incomes.types.other', icon: <GiftIcon /> }
];

// ===========================================
// 💰 COMPONENTE PRINCIPAL OPTIMIZADO PARA MÓVIL
// ===========================================
const FinIncome = ({ user, userProfile }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const colors = getGrayscalePalette(theme);
  
  // Estados para la gestión de ingresos
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ paycheck: 0, extra: 0, other: 0, total: 0 });
  
  // Estados para el formulario de ingreso
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    incomeType: 'paycheck',
    name: '',
    amount: '',
    date: new Date(),
    description: '',
    recurring: false
  });
  const [isEditing, setIsEditing] = useState(false);
  const [currentIncomeId, setCurrentIncomeId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Estados para la paginación y vista móvil
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredIncomes, setFilteredIncomes] = useState([]);
  
  // Filtrar ingresos basado en búsqueda
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredIncomes(incomes);
    } else {
      const filtered = incomes.filter(income => 
        income.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        income.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        getIncomeTypeName(income.incomeType).toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredIncomes(filtered);
    }
  }, [incomes, searchQuery]);

  // Ajustar rowsPerPage según el dispositivo
  useEffect(() => {
    setRowsPerPage(isMobile ? 5 : 10);
  }, [isMobile]);
  
  // Cargar datos al iniciar el componente
  useEffect(() => {
    const loadIncomeData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔄 Cargando datos de ingresos para usuario:', user?.uid);

        // Verificar que agtFinService y apiClient existen
        if (!agtFinService) {
          throw new Error('agtFinService no está disponible');
        }
        
        if (!agtFinService.apiClient) {
          throw new Error('apiClient no está inicializado en agtFinService');
        }

        // Obtener ingresos del mes actual y resumen usando el método correcto
        console.log('📅 Obteniendo ingresos del mes actual...');
        const currentMonthData = await agtFinService.getCurrentMonthIncomes();

        console.log('✅ Datos del mes actual recibidos:', currentMonthData);

        setSummary(currentMonthData.summary || { paycheck: 0, extra: 0, other: 0, total: 0 });
        
        // Obtener todos los ingresos usando el método correcto
        console.log('📋 Obteniendo todos los ingresos...');
        const allIncomesData = await agtFinService.getIncomes();
        
        console.log('✅ Todos los ingresos recibidos:', allIncomesData);
        
        setIncomes(allIncomesData.incomes || []);
        
      } catch (err) {
        console.error('❌ Error al cargar los datos de ingresos:', err);

        // Mensaje de error más específico
        let errorMessage = t('agtfin.incomes.errors.loadFailed');

        if (err.message.includes('apiClient')) {
          errorMessage = t('agtfin.incomes.errors.serviceConfig');
        } else if (err.response?.status === 401) {
          errorMessage = t('agtfin.incomes.errors.sessionExpired');
        } else if (err.code === 'ECONNREFUSED') {
          errorMessage = t('agtfin.incomes.errors.connectionError');
        } else if (err.userMessage) {
          errorMessage = err.userMessage;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (user?.uid) {
      loadIncomeData();
    } else {
      console.warn('⚠️ No hay usuario disponible para cargar ingresos');
      setLoading(false);
      setError(t('agtfin.incomes.errors.userNotAvailable'));
    }
  }, [user]);

  // Manejar apertura del formulario
  const handleOpenForm = () => {
    setFormData({
      incomeType: 'paycheck',
      name: '',
      amount: '',
      date: new Date(),
      description: '',
      recurring: false
    });
    setFormErrors({});
    setIsEditing(false);
    setCurrentIncomeId(null);
    setOpen(true);
  };

  // Manejar cierre del formulario
  const handleCloseForm = () => {
    setOpen(false);
    setFormErrors({});
    setSubmitLoading(false);
  };

  // Manejar cambios en el formulario
  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpiar error del campo si existe
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Manejar cambio de fecha
  const handleDateChange = (newDate) => {
    setFormData(prev => ({
      ...prev,
      date: newDate
    }));
    
    if (formErrors.date) {
      setFormErrors(prev => ({ ...prev, date: undefined }));
    }
  };

  // Validar formulario
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('agtfin.incomes.validation.nameRequired');
    }

    if (!formData.amount) {
      errors.amount = t('agtfin.incomes.validation.amountRequired');
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      errors.amount = t('agtfin.incomes.validation.amountPositive');
    }

    if (!formData.date) {
      errors.date = t('agtfin.incomes.validation.dateRequired');
    }

    return errors;
  };

  // Manejar envío del formulario
  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSubmitLoading(true);
      setError(null);
      
      // Formatear fecha correctamente
      let formattedDate;
      try {
        const dateObj = normalizeDateInput(formData.date);
        if (!dateObj) {
          throw new Error(t('agtfin.incomes.validation.invalidDate'));
        }
        formattedDate = formatLocalDateString(dateObj);
      } catch (dateError) {
        console.error('❌ Error formateando fecha:', dateError);
        setFormErrors({ date: t('agtfin.incomes.validation.invalidDateFormat') });
        return;
      }
      
      const incomeData = {
        incomeType: formData.incomeType,
        name: formData.name.trim(),
        amount: parseFloat(formData.amount),
        date: formattedDate,
        description: formData.description?.trim() || '',
        recurring: Boolean(formData.recurring)
      };
      
      console.log('💾 Enviando datos del ingreso:', incomeData);
      
      // Validación adicional en el frontend
      if (!incomeData.incomeType || !['paycheck', 'extra', 'other'].includes(incomeData.incomeType)) {
        setFormErrors({ incomeType: t('agtfin.incomes.validation.invalidType') });
        return;
      }

      if (!incomeData.name || incomeData.name.length === 0) {
        setFormErrors({ name: t('agtfin.incomes.validation.nameNotEmpty') });
        return;
      }

      if (isNaN(incomeData.amount) || incomeData.amount <= 0) {
        setFormErrors({ amount: t('agtfin.incomes.validation.amountPositive') });
        return;
      }

      if (!incomeData.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        setFormErrors({ date: t('agtfin.incomes.validation.invalidDateFormat') });
        return;
      }
      
      let result;
      
      if (isEditing && currentIncomeId) {
        console.log('🔄 Actualizando ingreso existente:', currentIncomeId);
        result = await agtFinService.updateIncome(currentIncomeId, incomeData);
      } else {
        console.log('➕ Creando nuevo ingreso');
        result = await agtFinService.createIncome(incomeData);
      }
      
      console.log('✅ Operación exitosa:', result);
      
      handleCloseForm();
      
      // Recargar datos después de guardar
      console.log('🔄 Recargando datos después de guardar...');
      await reloadIncomeData();
      
    } catch (err) {
      console.error('❌ Error al guardar el ingreso:', err);

      // Mensaje de error más específico
      let errorMessage = t('agtfin.incomes.errors.saveFailed');

      if (err.response?.status === 400) {
        // Intentar extraer detalles de validación específicos
        const validationErrors = err.response.data?.details;
        if (validationErrors && Array.isArray(validationErrors)) {
          // Mostrar el primer error de validación
          errorMessage = validationErrors[0]?.message || err.response.data?.error || 'Datos inválidos. Verifica la información.';
        } else {
          errorMessage = err.response.data?.error || 'Datos inválidos. Verifica la información.';
        }
      } else if (err.response?.status === 401) {
        errorMessage = t('agtfin.incomes.errors.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Función auxiliar para recargar datos
  const reloadIncomeData = async () => {
    try {
      console.log('🔄 Recargando datos de ingresos...');
      
      // Recargar ingresos del mes actual
      const currentMonthData = await agtFinService.getCurrentMonthIncomes();
      setSummary(currentMonthData.summary || { paycheck: 0, extra: 0, other: 0, total: 0 });
      
      // Recargar todos los ingresos
      const allIncomesData = await agtFinService.getIncomes();
      setIncomes(allIncomesData.incomes || []);
      
      console.log('✅ Datos recargados exitosamente');
      
    } catch (err) {
      console.error('❌ Error recargando datos:', err);
      setError(t('agtfin.incomes.errors.reloadFailed'));
    }
  };

  // Manejar edición de ingreso
  const handleEditIncome = (income) => {
    console.log('✏️ Editando ingreso:', income);
    
    setFormData({
      incomeType: income.incomeType || 'other',
      name: income.name || '',
      amount: income.amount.toString(),
      date: parseLocalDate(income.date) || new Date(),
      description: income.description || '',
      recurring: income.recurring || false
    });
    setIsEditing(true);
    setCurrentIncomeId(income.incomeId);
    setFormErrors({});
    setOpen(true);
  };

  // Manejar eliminación de ingreso
  const handleDeleteIncome = async (incomeId) => {
    if (!window.confirm(t('agtfin.incomes.actions.confirmDelete'))) {
      return;
    }

    try {
      console.log('🗑️ Eliminando ingreso:', incomeId);

      await agtFinService.deleteIncome(incomeId);

      console.log('✅ Ingreso eliminado exitosamente');

      // Recargar datos después de eliminar
      await reloadIncomeData();

    } catch (err) {
      console.error('❌ Error al eliminar el ingreso:', err);

      let errorMessage = t('agtfin.incomes.errors.deleteFailed');

      if (err.response?.status === 404) {
        errorMessage = t('agtfin.incomes.errors.notFound');
      } else if (err.response?.status === 401) {
        errorMessage = t('agtfin.incomes.errors.sessionExpired');
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
    }
  };

  // Manejar cambio de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Manejar cambio de filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Formatear moneda
  const formatCurrency = (amount) => {
    return agtFinService.formatCurrency(amount || 0, userProfile?.currency || 'USD');
  };

  // Obtener ícono según tipo de ingreso
  const getIncomeTypeIcon = (type) => {
    const incomeType = incomeTypes.find(it => it.value === type);
    return incomeType ? incomeType.icon : <MoneyIcon />;
  };

  // Obtener nombre según tipo de ingreso
  const getIncomeTypeName = (type) => {
    const incomeType = incomeTypes.find(it => it.value === type);
    return incomeType ? t(incomeType.labelKey) : t('agtfin.incomes.types.other');
  };

  // Mostrar spinner mientras carga
  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        flexDirection: 'column',
        gap: 2,
        px: 2
      }}>
        <CircularProgress size={48} sx={{ color: colors.primary }} />
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {t('agtfin.incomes.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Encabezado adaptativo - ACTUALIZADO: Con botón FAB integrado en móvil */}
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'center' : 'center', 
        mb: 3,
        flexDirection: 'row'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Box sx={{
            p: isMobile ? 1.2 : 1.5,
            borderRadius: '12px',
            background: colors.gradients.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AnalyticsIcon sx={{ 
              fontSize: isMobile ? 20 : 24, 
              color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
            }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              fontWeight={700}
              sx={{ color: colors.primary }}
            >
              {t('agtfin.incomes.title')}
            </Typography>
            <Typography
              variant={isMobile ? "body2" : "subtitle1"}
              color="text.secondary"
              fontWeight={500}
            >
              {t('agtfin.incomes.subtitle')}
            </Typography>
          </Box>
        </Box>
        
        {/* Botón FAB integrado para móvil y botón normal para desktop */}
        {isMobile ? (
          <MobileFab 
            onClick={handleOpenForm}
            disabled={submitLoading}
          >
            <AddIcon />
          </MobileFab>
        ) : (
          <ResponsiveButton 
            startIcon={<AddIcon />}
            onClick={handleOpenForm}
            disabled={submitLoading}
          >
            {t('agtfin.incomes.new')}
          </ResponsiveButton>
        )}
      </Box>

      {/* Mensaje de error */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            fontWeight: 500
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* 1. SECCIÓN DE TOTALES */}
      <Box component={motion.div} variants={itemVariants}>
        <MobileTotalsSection
          summary={summary}
          userProfile={userProfile}
          theme={theme}
        />
      </Box>

      {/* 2. SECCIÓN DE DISTRIBUCIÓN */}
      <Box component={motion.div} variants={itemVariants}>
        <MobileDistributionSection
          summary={summary}
          userProfile={userProfile}
          theme={theme}
        />
      </Box>

      {/* 3. SECCIÓN DE HISTORIAL */}
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{ mb: 4 }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0
        }}>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight={700}
            sx={{ color: colors.primary }}
          >
            {t('agtfin.incomes.history')}
          </Typography>
          
          {/* Barra de búsqueda para móvil - ACTUALIZADA: Sin borde y sin hover */}
          {isMobile && (
            <ResponsiveTextField
              noborder={true}
              size="small"
              placeholder={t('agtfin.incomes.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
              sx={{ width: '100%', maxWidth: 300 }}
            />
          )}
        </Box>
        
        {/* Vista móvil: Lista de cards */}
        {isMobile ? (
          <MobileIncomeList 
            incomes={filteredIncomes}
            formatCurrency={formatCurrency}
            getIncomeTypeIcon={getIncomeTypeIcon}
            getIncomeTypeName={getIncomeTypeName}
            handleEditIncome={handleEditIncome}
            handleDeleteIncome={handleDeleteIncome}
            submitLoading={submitLoading}
            colors={colors}
            theme={theme}
          />
        ) : (
          /* Vista desktop: Tabla tradicional */
          <ResponsiveTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('agtfin.incomes.table.type')}</TableCell>
                  <TableCell>{t('agtfin.incomes.table.name')}</TableCell>
                  <TableCell>{t('agtfin.incomes.table.amount')}</TableCell>
                  <TableCell>{t('agtfin.incomes.table.date')}</TableCell>
                  <TableCell>{t('agtfin.incomes.table.description')}</TableCell>
                  <TableCell align="right">{t('agtfin.incomes.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredIncomes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        gap: 3
                      }}>
                        <Box sx={{
                          p: 3,
                          borderRadius: '50%',
                          background: colors.backgrounds.light,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <MoneyIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            {t('agtfin.incomes.empty')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('agtfin.incomes.emptyHelp')}
                          </Typography>
                        </Box>
                        <ResponsiveButton
                          startIcon={<AddIcon />}
                          onClick={handleOpenForm}
                          size="small"
                        >
                          {t('agtfin.incomes.addFirst')}
                        </ResponsiveButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredIncomes
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((income) => (
                      <TableRow key={income.incomeId}>
                        <TableCell>
                          <ResponsiveChip 
                            icon={getIncomeTypeIcon(income.incomeType)}
                            label={getIncomeTypeName(income.incomeType)}
                            size="small"
                            chiptype={income.incomeType}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {income.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} sx={{ color: colors.primary }}>
                            {formatCurrency(income.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {parseLocalDate(income.date)
                              ? format(parseLocalDate(income.date), 'dd/MM/yyyy')
                              : income.date}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {income.description ? (
                            <Tooltip title={income.description} arrow>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  maxWidth: 150,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {income.description}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', opacity: 0.6 }}>
                              {t('agtfin.incomes.noDescription')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title={t('agtfin.incomes.actions.edit')} arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleEditIncome(income)}
                                disabled={submitLoading}
                                sx={{
                                  borderRadius: 2,
                                  background: colors.backgrounds.light,
                                  '&:hover': {
                                    background: colors.backgrounds.medium,
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <EditIcon fontSize="small" sx={{ color: colors.primary }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('agtfin.incomes.actions.delete')} arrow>
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteIncome(income.incomeId)}
                                disabled={submitLoading}
                                sx={{
                                  borderRadius: 2,
                                  background: colors.backgrounds.light,
                                  '&:hover': {
                                    background: colors.backgrounds.medium,
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <DeleteIcon fontSize="small" sx={{ color: colors.error }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
            
            {filteredIncomes.length > 0 && (
              <TablePagination
                rowsPerPageOptions={isTablet ? [5, 10] : [5, 10, 25]}
                component="div"
                count={filteredIncomes.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('agtfin.incomes.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => t('agtfin.incomes.pagination.displayedRows', { from, to, count })}
                sx={{
                  borderTop: `1px solid ${colors.backgrounds.medium}`,
                  '& .MuiTablePagination-toolbar': {
                    padding: isTablet ? 1 : 2,
                    flexDirection: isTablet ? 'column' : 'row',
                    gap: isTablet ? 1 : 0,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontWeight: 500,
                    color: colors.secondary,
                    fontSize: isTablet ? '0.8rem' : '0.875rem'
                  },
                  '& .MuiTablePagination-select': {
                    fontSize: isTablet ? '0.8rem' : '0.875rem'
                  }
                }}
              />
            )}
          </ResponsiveTableContainer>
        )}
      </Box>

      {/* Diálogo de formulario optimizado para móvil */}
      <MobileDialog 
        open={open} 
        onClose={handleCloseForm} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        {/* AppBar para móvil */}
        {isMobile && (
          <AppBar 
            sx={{ 
              position: 'relative',
              background: colors.gradients.primary,
              color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'
            }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleCloseForm}
                disabled={submitLoading}
              >
                <CloseIcon />
              </IconButton>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div" fontWeight={600}>
                {isEditing ? t('agtfin.incomes.form.editTitle') : t('agtfin.incomes.form.newTitle')}
              </Typography>
              <ResponsiveButton 
                onClick={handleSubmit} 
                disabled={submitLoading}
                size="small"
                sx={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'inherit',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                {submitLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  isEditing ? t('agtfin.incomes.form.update') : t('agtfin.incomes.form.save')
                )}
              </ResponsiveButton>
            </Toolbar>
          </AppBar>
        )}

        {/* Título para desktop */}
        {!isMobile && (
          <DialogTitle sx={{ 
            pb: 2, 
            borderBottom: `1px solid ${colors.backgrounds.medium}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{
              p: 1,
              borderRadius: '8px',
              background: colors.gradients.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {isEditing ? (
                <EditIcon sx={{ 
                  fontSize: 20, 
                  color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                }} />
              ) : (
                <AddIcon sx={{ 
                  fontSize: 20, 
                  color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' 
                }} />
              )}
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {isEditing ? t('agtfin.incomes.form.editTitle') : t('agtfin.incomes.form.newTitle')}
            </Typography>
          </DialogTitle>
        )}
        
        <DialogContent sx={{ 
          pt: isMobile ? 2 : 3,
          px: isMobile ? 2 : 3,
          pb: isMobile ? 2 : 3
        }}>
          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid item xs={12} sm={6}>
              <ResponsiveTextField
                select
                fullWidth
                label={t('agtfin.incomes.form.typeLabel')}
                name="incomeType"
                value={formData.incomeType}
                onChange={handleFormChange}
                error={!!formErrors.incomeType}
                helperText={formErrors.incomeType}
                size={isMobile ? "medium" : "medium"}
              >
                {incomeTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.cloneElement(option.icon, { fontSize: 'small' })}
                      {t(option.labelKey)}
                    </Box>
                  </MenuItem>
                ))}
              </ResponsiveTextField>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <ResponsiveTextField
                fullWidth
                label={t('agtfin.incomes.form.nameLabel')}
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
                placeholder={t('agtfin.incomes.form.namePlaceholder')}
                size={isMobile ? "medium" : "medium"}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <ResponsiveTextField
                fullWidth
                label={t('agtfin.incomes.form.amountLabel')}
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleFormChange}
                error={!!formErrors.amount}
                helperText={formErrors.amount}
                size={isMobile ? "medium" : "medium"}
                InputProps={{
                  startAdornment: (
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ mr: 1, fontWeight: 600 }}
                    >
                      {userProfile?.currency === 'USD' ? '$' : 
                       userProfile?.currency === 'EUR' ? '€' : 
                       userProfile?.currency === 'CLP' ? '$' : 
                       userProfile?.currency === 'MXN' ? '$' : 
                       userProfile?.currency === 'ARS' ? '$' : 
                       userProfile?.currency === 'COP' ? '$' : 
                       userProfile?.currency || '$'}
                    </Typography>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <DatePicker
                  label={t('agtfin.incomes.form.dateLabel')}
                  value={formData.date}
                  onChange={handleDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.date,
                      helperText: formErrors.date,
                      size: isMobile ? "medium" : "medium",
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: isMobile ? 2.5 : 3,
                          background: colors.backgrounds.light,
                          fontSize: isMobile ? '16px' : '14px', // Evita zoom en iOS
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12}>
              <ResponsiveTextField
                fullWidth
                label={t('agtfin.incomes.form.descriptionLabel')}
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                multiline
                rows={isMobile ? 2 : 3}
                placeholder={t('agtfin.incomes.form.descriptionPlaceholder')}
                size={isMobile ? "medium" : "medium"}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        {/* Acciones solo para desktop */}
        {!isMobile && (
          <DialogActions sx={{ 
            p: 3, 
            borderTop: `1px solid ${colors.backgrounds.medium}`,
            gap: 2
          }}>
            <Button
              onClick={handleCloseForm}
              disabled={submitLoading}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                color: colors.secondary,
                '&:hover': {
                  background: colors.backgrounds.light
                }
              }}
            >
              {t('agtfin.incomes.form.cancel')}
            </Button>
            <ResponsiveButton
              onClick={handleSubmit}
              disabled={submitLoading}
              startIcon={submitLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                isEditing ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />
              )}
            >
              {submitLoading ? t('agtfin.incomes.form.saving') : (isEditing ? t('agtfin.incomes.form.update') : t('agtfin.incomes.form.save'))}
            </ResponsiveButton>
          </DialogActions>
        )}
      </MobileDialog>
    </ResponsiveContainer>
  );
};

export default FinIncome;
